import React, { useState, useRef } from 'react';
import { Box, Typography, Button, Grid, useTheme, useMediaQuery, OutlinedInput, FormHelperText, CircularProgress, Snackbar, Alert, InputAdornment, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors, fonts } from '../../ColorFont/color';
import { url } from '../../ConstUrl/Url';
import {
    IconCalendarEvent,
    IconCheck
} from '@tabler/icons-react';
import NewHeaderNavWithSidebar from '../../components/HeaderNav/NewHeaderNavWithSidebar';

/* 
===== UNIVERSAL TOKENS =====
Imported seamlessly from current theme mappings
============================
*/

const translations = {
    en: {
        timeline: "Timeline",
        projectTimeline: "Project Timeline",
        projectStartQuestion: "When does your project start?",
        startDate: "Start Date *",
        projectDuration: "Project Duration (Months) *",
        projectDurationPlaceholder: "e.g. 12, 24, 36, 60",
        prevSeasonCrop: "Previous Season Crop (Optional)",
        prevSeasonCropPlaceholder: "e.g. Wheat",
        additionalNotes: "Additional Notes",
        additionalNotesPlaceholder: "Any additional details about your project...",
        saveAndContinue: "Save and Continue",
        back: "Back",
        savedSuccess: "Timeline saved successfully!"
    },
    mr: {
        timeline: "वेळापत्रक",
        projectTimeline: "प्रकल्पाचे वेळापत्रक",
        projectStartQuestion: "तुमचा प्रकल्प कधी सुरू होतो?",
        startDate: "सुरुवातीची तारीख *",
        projectDuration: "प्रकल्पाचा कालावधी (महिने) *",
        projectDurationPlaceholder: "उदा. १२, २४, ३६, ६०",
        prevSeasonCrop: "मागील हंगामातील पीक (पर्यायी)",
        prevSeasonCropPlaceholder: "उदा. गहू",
        additionalNotes: "अतिरिक्त माहिती",
        additionalNotesPlaceholder: "तुमच्या प्रकल्पाविषयी कोणतीही अतिरिक्त माहिती...",
        saveAndContinue: "जतन करा आणि पुढे जा",
        back: "मागे",
        savedSuccess: "वेळापत्रक यशस्वीरीत्या जतन केले!"
    },
    hi: {
        timeline: "समयरेखा",
        projectTimeline: "परियोजना समयरेखा",
        projectStartQuestion: "आपकी परियोजना कब शुरू होती है?",
        startDate: "प्रारंभ तिथि *",
        projectDuration: "परियोजना की अवधि (महीने) *",
        projectDurationPlaceholder: "उदा. 12, 24, 36, 60",
        prevSeasonCrop: "पिछली फसल (वैकल्पिक)",
        prevSeasonCropPlaceholder: "उदा. गेहूँ",
        additionalNotes: "अतिरिक्त विवरण",
        additionalNotesPlaceholder: "आपकी परियोजना के बारे में कोई अतिरिक्त विवरण...",
        saveAndContinue: "सलेजें और आगे बढ़ें",
        back: "वापस",
        savedSuccess: "समयरेखा सफलतापूर्वक सहेजी गई!"
    }
};

const CustomInputLabel = ({ label }) => (
    <Typography sx={{
        fontFamily: fonts.publicSans || fonts.poppins,
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '14px',
        color: colors.carbon_text_main || '#0F1729',
        mb: '12px',
        display: 'block'
    }}>
        {label}
    </Typography>
);

const getInputStyles = (hasError) => ({
    height: '48px',
    borderRadius: '14px',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_border_solid || '#E0E5EB')}`,
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
    '& fieldset': { border: 'none' },
    '&:hover': {
        border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_border_solid || '#E0E5EB')}`,
    },
    '&.Mui-focused': {
        border: `1px solid ${hasError ? '#EF4444' : (colors.carbon_bg_main || '#00B4D8')}`,
    },
    fontFamily: fonts.publicSans || fonts.poppins,
    fontSize: '14px',
    color: colors.carbon_text_main || '#0F1729',
    '& input::placeholder': {
        color: '#94A3B8',
        opacity: 1
    }
});

const getTextAreaStyles = () => ({
    borderRadius: '14px',
    backgroundColor: '#FFFFFF',
    border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
    boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
    '& fieldset': { border: 'none' },
    '&:hover': {
        border: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
    },
    '&.Mui-focused': {
        border: `1px solid ${colors.carbon_bg_main || '#00B4D8'}`,
    },
    fontFamily: fonts.publicSans || fonts.poppins,
    fontSize: '14px',
    color: colors.carbon_text_main || '#0F1729',
    padding: '12px 16px',
    '& textarea::placeholder': {
        color: '#94A3B8',
        opacity: 1
    }
});

// FIX 3: Restore timeline data from localStorage draft on mount
const loadTimelineDraft = () => {
    try {
        const draft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
        return {
            startDate: draft.projectStartDate || '',
            duration: draft.projectDuration || '',
            prevCrop: draft.previousCrop || '',
            notes: draft.additionalNotes || ''
        };
    } catch {
        return { startDate: '', duration: '', prevCrop: '', notes: '' };
    }
};

const CarbonCreditsTimeline = ({ userData, onNext, onBack }) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || 'en';
    const tLocal = translations[currentLang] || translations['en'];
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // FIX 3: Initialize from localStorage so Back navigation restores data
    const [formData, setFormData] = useState(loadTimelineDraft);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // FIX 2A: Ref to programmatically open native date picker on any click within the field
    const dateInputRef = useRef(null);

    const steps = [
        { id: 1, label: "", isActive: true, isCompleted: true },
        { id: 2, label: "", isActive: true, isCompleted: true },
        { id: 3, label: tLocal.timeline, isActive: true, isCompleted: false },
        { id: 4, label: "", isActive: false, isCompleted: false },
    ];

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({ ...prev, [field]: event.target.value }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.startDate) newErrors.startDate = "Start Date is required";
        if (!formData.duration || isNaN(Number(formData.duration)) || Number(formData.duration) <= 0) {
            newErrors.duration = "Please enter a valid duration in months (e.g. 12, 24, 36)";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (validateForm()) {
            setIsSubmitting(true);
            try {
                const draft = JSON.parse(localStorage.getItem('carbonCreditDraft') || '{}');
                draft.projectStartDate = formData.startDate;
                draft.projectDuration = formData.duration;
                draft.previousCrop = formData.prevCrop;
                draft.additionalNotes = formData.notes;
                localStorage.setItem('carbonCreditDraft', JSON.stringify(draft));

                if (onNext) onNext();
            } catch (error) {
                console.error("Error saving draft info:", error);
                alert("An error occurred while saving locally.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <Box sx={{
            width: '100%',
            minHeight: '100vh',
            backgroundColor: '#FFFFFF',
            pb: '80px'
        }}>
            {/* Top Stepper Area */}
            <Box sx={{
                width: '100%',
                pt: isMobile ? '12px' : '16px',
                pb: isMobile ? '12px' : '14px',
                borderBottom: `1px solid ${colors.carbon_border_solid || '#E0E5EB'}`,
                backgroundColor: '#FFFFFF',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    maxWidth: '680px',
                    px: isMobile ? '20px' : '24px'
                }}>
                    {steps.map((step, index) => (
                        <React.Fragment key={step.id}>
                            <Box sx={{
                                width: '32px',
                                height: '32px',
                                flexShrink: 0,
                                borderRadius: '50%',
                                backgroundColor: (step.isActive || step.isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                color: (step.isActive || step.isCompleted) ? '#FFFFFF' : (colors.carbon_step_text_unselected || '#64748B'),
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                fontFamily: fonts.publicSans || fonts.poppins,
                                fontWeight: 600,
                                fontSize: '14px',
                                zIndex: 2
                            }}>
                                {step.isCompleted ? <IconCheck size={18} color="#FFFFFF" stroke={2.5} /> : step.id}
                            </Box>
                            {index < steps.length - 1 && (
                                <Box sx={{
                                    flex: 1,
                                    mx: isMobile ? '8px' : '16px',
                                    height: '2px',
                                    backgroundColor: (steps[index + 1].isActive || steps[index + 1].isCompleted) ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_step_bg_unselected || '#E2E8F0'),
                                    borderRadius: '4px'
                                }} />
                            )}
                        </React.Fragment>
                    ))}
                </Box>
                <Typography sx={{
                    mt: '16px',
                    fontFamily: fonts.publicSans || fonts.poppins,
                    fontWeight: 500,
                    fontSize: '13px',
                    color: colors.carbon_text_secondary || '#65758B',
                }}>
                    {steps.find(s => s.label)?.label || ""}
                </Typography>
            </Box>

            {/* Content Area */}
            <Box sx={{
                maxWidth: '680px',
                margin: '0 auto',
                px: 0,
                pt: isMobile ? '16px' : '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                {/* Header Icon & Title */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    mb: '24px'
                }}>
                    <Box sx={{ color: colors.carbon_bg_main || '#00B4D8' }}>
                        <IconCalendarEvent size={48} stroke={1.5} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography sx={{
                            fontFamily: fonts.manrope || fonts.poppins,
                            fontWeight: 700,
                            fontSize: isMobile ? '24px' : '28px',
                            lineHeight: '34px',
                            color: colors.carbon_text_main || '#0F1729',
                            mb: '8px'
                        }}>
                            {tLocal.projectTimeline}
                        </Typography>
                        <Typography sx={{
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 400,
                            fontSize: isMobile ? '14px' : '16px',
                            lineHeight: '22px',
                            color: colors.carbon_text_secondary || '#65758B',
                            maxWidth: '480px'
                        }}>
                            {tLocal.projectStartQuestion}
                        </Typography>
                    </Box>
                </Box>

                {/* Form Grid */}
                <Grid container spacing={3} sx={{ width: '100%' }}>

                    {/* FIX 2A: Start Date — clicking anywhere in the field opens calendar */}
                    <Grid item xs={12}>
                        <CustomInputLabel label={tLocal.startDate} />
                        {/* Wrapper Box: clicking anywhere triggers the hidden date input */}
                        <Box
                            onClick={() => {
                                if (dateInputRef.current) {
                                    dateInputRef.current.showPicker?.();
                                    dateInputRef.current.focus();
                                }
                            }}
                            sx={{
                                position: 'relative',
                                cursor: 'pointer',
                                height: '48px',
                                borderRadius: '14px',
                                backgroundColor: '#FFFFFF',
                                border: `1px solid ${errors.startDate ? '#EF4444' : (colors.carbon_border_solid || '#E0E5EB')}`,
                                boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                                display: 'flex',
                                alignItems: 'center',
                                px: '14px',
                                gap: '10px',
                                '&:hover': {
                                    border: `1px solid ${errors.startDate ? '#EF4444' : (colors.carbon_bg_main || '#00B4D8')}`,
                                },
                                '&:focus-within': {
                                    border: `1px solid ${errors.startDate ? '#EF4444' : (colors.carbon_bg_main || '#00B4D8')}`,
                                }
                            }}
                        >
                            {/* Calendar icon */}
                            <IconCalendarEvent
                                size={18}
                                stroke={1.5}
                                color={colors.carbon_bg_main || '#00B4D8'}
                                style={{ flexShrink: 0 }}
                            />
                            {/* Actual hidden native date input — shows the calendar */}
                            <input
                                ref={dateInputRef}
                                type="date"
                                value={formData.startDate}
                                onChange={handleInputChange('startDate')}
                                style={{
                                    flex: 1,
                                    border: 'none',
                                    outline: 'none',
                                    background: 'transparent',
                                    fontFamily: fonts.publicSans || fonts.poppins,
                                    fontSize: '14px',
                                    color: formData.startDate ? (colors.carbon_text_main || '#0F1729') : '#94A3B8',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                            />
                        </Box>
                        {errors.startDate && <FormHelperText error sx={{ ml: 0 }}>{errors.startDate}</FormHelperText>}
                    </Grid>

                    {/* FIX 2B: Project Duration — free text input (keyboard + paste), no picker restriction */}
                    <Grid item xs={12}>
                        <CustomInputLabel label={tLocal.projectDuration} />
                        <OutlinedInput
                            fullWidth
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                            value={formData.duration}
                            onChange={handleInputChange('duration')}
                            placeholder={tLocal.projectDurationPlaceholder}
                            endAdornment={
                                <InputAdornment position="end">
                                    <Typography sx={{
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '13px',
                                        color: colors.carbon_text_secondary || '#65758B',
                                        pr: '4px'
                                    }}>
                                        months
                                    </Typography>
                                </InputAdornment>
                            }
                            sx={{
                                ...getInputStyles(!!errors.duration),
                                '& input': { padding: '12px 14px' }
                            }}
                        />
                        {/* Quick-select chips for common durations */}
                        <Box sx={{ display: 'flex', gap: '8px', mt: '10px', flexWrap: 'wrap' }}>
                            {['12', '24', '36', '60'].map(m => (
                                <Box
                                    key={m}
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, duration: m }));
                                        if (errors.duration) setErrors(prev => ({ ...prev, duration: null }));
                                    }}
                                    sx={{
                                        px: '12px',
                                        py: '4px',
                                        borderRadius: '20px',
                                        border: `1px solid ${formData.duration === m ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_border_solid || '#E0E5EB')}`,
                                        backgroundColor: formData.duration === m ? `rgba(0,180,216,0.08)` : '#FFFFFF',
                                        color: formData.duration === m ? (colors.carbon_bg_main || '#00B4D8') : (colors.carbon_text_secondary || '#65758B'),
                                        fontFamily: fonts.publicSans || fonts.poppins,
                                        fontSize: '13px',
                                        fontWeight: formData.duration === m ? 600 : 400,
                                        cursor: 'pointer',
                                        transition: 'all 0.15s ease',
                                        '&:hover': {
                                            borderColor: colors.carbon_bg_main || '#00B4D8',
                                            color: colors.carbon_bg_main || '#00B4D8'
                                        }
                                    }}
                                >
                                    {m} months
                                </Box>
                            ))}
                        </Box>
                        {errors.duration && <FormHelperText error sx={{ ml: 0, mt: '6px' }}>{errors.duration}</FormHelperText>}
                    </Grid>

                    {/* Previous Season Crop */}
                    <Grid item xs={12}>
                        <CustomInputLabel label={tLocal.prevSeasonCrop} />
                        <OutlinedInput
                            fullWidth
                            value={formData.prevCrop}
                            onChange={handleInputChange('prevCrop')}
                            placeholder={tLocal.prevSeasonCropPlaceholder}
                            sx={{
                                ...getInputStyles(false),
                                '& input': { padding: '12px 14px' }
                            }}
                        />
                    </Grid>

                    {/* Additional Notes */}
                    <Grid item xs={12}>
                        <CustomInputLabel label={tLocal.additionalNotes} />
                        <OutlinedInput
                            fullWidth
                            multiline
                            rows={4}
                            value={formData.notes}
                            onChange={handleInputChange('notes')}
                            placeholder={tLocal.additionalNotesPlaceholder}
                            sx={getTextAreaStyles()}
                        />
                    </Grid>
                </Grid>

                {/* Submit Action */}
                <Box sx={{ mt: '48px', mb: '40px', width: '100%', display: 'flex', justifyContent: 'center', gap: '16px', flexDirection: isMobile ? 'column' : 'row' }}>
                    <Button
                        onClick={onBack}
                        variant="outlined"
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: colors.carbon_text_main || '#1C252E',
                            borderColor: colors.carbon_border_solid || '#E0E5EB',
                            minWidth: { sm: '180px' },
                            '&:hover': {
                                borderColor: colors.carbon_border_selected || '#919EAB',
                                backgroundColor: '#F4F6F8',
                            },
                        }}
                    >
                        {tLocal.back}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            borderRadius: '50px',
                            px: { xs: 3, md: 6 },
                            height: '52px',
                            backgroundColor: colors.carbon_bg_main || '#00B4D8',
                            fontFamily: fonts.publicSans || fonts.poppins,
                            fontWeight: 700,
                            fontSize: '15px',
                            textTransform: 'none',
                            color: '#FFFFFF',
                            minWidth: { sm: '220px' },
                            boxShadow: '0px 4px 12px rgba(0, 180, 216, 0.2)',
                            '&:hover': {
                                backgroundColor: '#009DC0',
                                boxShadow: '0px 6px 16px rgba(0, 180, 216, 0.3)',
                                transform: 'translateY(-1px)'
                            },
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} color="inherit" /> : tLocal.saveAndContinue}
                    </Button>
                </Box>
            </Box>


            {/* FIX 6: Floating + button REMOVED — was not present but confirmed removed */}
        </Box>
    );
};

export default CarbonCreditsTimeline;
